/**
 * @copyright
 * Copyright (c) 2025 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
import { createSlice } from "@reduxjs/toolkit";
import cloneDeep from "lodash/fp/cloneDeep";

import { Interval, RequestStatus } from "~/constants";
import {
  loadDashboardReportAsync,
  loadGeoLocationAsync,
  loadPlatformStatsAsync,
  loadQuickStatsAsync,
  loadSitesAsync,
  loadStatsByIntervalAsync,
} from "~/features/dashboard/thunks";
import { DashboardState } from "~/features/dashboard/types";
import { ReportStatusEnum } from "~/features/reportTracker/constants";

const initialState: DashboardState = {
  statsOptions: {
    selectedInterval: null,
    excludedSites: [],
  },
  quickStats: {
    meta: {
      status: RequestStatus.IDLE,
      lastFetched: null,
    },
    data: {
      lastThirtyDaysData: null,
      previousPeriodPayout: null,
      currentPeriodPayout: null,
      yesterdayData: null,
      todayData: null,
      payoutProgram: false,
      hasSites: false,
    },
  },
  stats: {
    meta: {
      status: RequestStatus.IDLE,
      lastFetched: null,
    },
    data: {
      byDate: {},
      sortedDates: [],
      sites: [],
    },
  },
  referrerSites: {
    meta: {
      status: RequestStatus.IDLE,
      lastFetched: null,
    },
    data: {
      sites: [],
    },
  },
  platformStats: {
    meta: {
      status: RequestStatus.IDLE,
      lastFetched: null,
    },
    data: {
      devices: {
        byDeviceType: {},
        sortedDeviceTypes: [],
      },
    },
  },
  geoLocation: {
    meta: {
      status: RequestStatus.IDLE,
      lastFetched: null,
    },
    data: {
      countries: {
        byCountryCode: {},
        sortedCountryCodes: [],
      },
    },
  },
};

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    setSelectedInterval: (state, action) => {
      state.statsOptions.selectedInterval = action.payload as Interval;
    },
    setExcludedSites: (state, action) => {
      state.statsOptions.excludedSites = action.payload as string[];
    },
    clearData: (state) => {
      state.stats = cloneDeep(initialState.stats);
      state.geoLocation = cloneDeep(initialState.geoLocation);
      state.platformStats = cloneDeep(initialState.platformStats);
    },
    resetDashboard: (state) => {
      state.statsOptions = cloneDeep(initialState.statsOptions);
      state.quickStats = cloneDeep(initialState.quickStats);
      state.stats = cloneDeep(initialState.stats);
      state.referrerSites = cloneDeep(initialState.referrerSites);
      state.platformStats = cloneDeep(initialState.platformStats);
      state.geoLocation = cloneDeep(initialState.geoLocation);
    },
    setDashboardStatus: (state, action: { payload: RequestStatus }) => {
      state.stats.meta.status = action.payload;
      state.platformStats.meta.status = action.payload;
      state.geoLocation.meta.status = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder

      // Quick Stats
      .addCase(loadQuickStatsAsync.pending, (state) => {
        const { quickStats } = state;

        quickStats.meta.status = RequestStatus.PENDING;
        quickStats.meta.lastFetched = Date.now();
      })

      .addCase(loadQuickStatsAsync.fulfilled, (state, action) => {
        const { quickStats } = state;

        quickStats.meta.status = RequestStatus.SUCCESS;
        quickStats.data = action.payload;
      })

      .addCase(loadQuickStatsAsync.rejected, (state) => {
        const { quickStats } = state;

        quickStats.meta.status = RequestStatus.ERROR;
      })

      // Stats by interval
      .addCase(loadStatsByIntervalAsync.pending, (state) => {
        const { stats } = state;

        stats.meta.status = RequestStatus.PENDING;
        stats.meta.lastFetched = Date.now();
      })

      .addCase(loadStatsByIntervalAsync.fulfilled, (state, action) => {
        const { stats } = state;

        stats.meta.status = RequestStatus.SUCCESS;
        stats.data = action.payload;
      })

      .addCase(loadStatsByIntervalAsync.rejected, (state) => {
        const { stats } = state;

        stats.meta.status = RequestStatus.ERROR;
      })

      // Referrer Sites
      .addCase(loadSitesAsync.pending, (state) => {
        const { referrerSites } = state;

        referrerSites.meta.status = RequestStatus.PENDING;
        referrerSites.meta.lastFetched = Date.now();
      })

      .addCase(loadSitesAsync.fulfilled, (state, action) => {
        const { referrerSites } = state;

        referrerSites.meta.status = RequestStatus.SUCCESS;
        referrerSites.data = action.payload;
      })

      .addCase(loadSitesAsync.rejected, (state) => {
        const { referrerSites } = state;

        referrerSites.meta.status = RequestStatus.ERROR;
      })

      //Platform Stats
      .addCase(loadPlatformStatsAsync.pending, (state) => {
        const { platformStats } = state;

        platformStats.meta.status = RequestStatus.PENDING;
        platformStats.meta.lastFetched = Date.now();
      })

      .addCase(loadPlatformStatsAsync.fulfilled, (state, action) => {
        const { platformStats } = state;

        platformStats.meta.status = RequestStatus.SUCCESS;
        platformStats.data = action.payload;
      })

      .addCase(loadPlatformStatsAsync.rejected, (state) => {
        const { platformStats } = state;

        platformStats.meta.status = RequestStatus.ERROR;
      })

      // Geo Location
      .addCase(loadGeoLocationAsync.pending, (state) => {
        const { geoLocation } = state;

        geoLocation.meta.status = RequestStatus.PENDING;
        geoLocation.meta.lastFetched = Date.now();
      })

      .addCase(loadGeoLocationAsync.fulfilled, (state, action) => {
        const { geoLocation } = state;

        geoLocation.meta.status = RequestStatus.SUCCESS;
        geoLocation.data = action.payload;
      })

      .addCase(loadGeoLocationAsync.rejected, (state) => {
        const { geoLocation } = state;

        geoLocation.meta.status = RequestStatus.ERROR;
      })

      .addCase(loadDashboardReportAsync.pending, (state) => {
        const { stats, platformStats, geoLocation } = state;

        stats.meta.status = RequestStatus.PENDING;
        platformStats.meta.status = RequestStatus.PENDING;
        geoLocation.meta.status = RequestStatus.PENDING;
      })

      .addCase(loadDashboardReportAsync.fulfilled, (state, action) => {
        const { stats, platformStats, geoLocation } = state;

        if (action.payload.status === ReportStatusEnum.PENDING) {
          stats.meta.status = RequestStatus.PENDING;
          platformStats.meta.status = RequestStatus.PENDING;
          geoLocation.meta.status = RequestStatus.PENDING;
          return;
        }

        stats.data = action.payload.totalStats;
        stats.meta.status = RequestStatus.SUCCESS;

        platformStats.data = action.payload.platformStats;
        platformStats.meta.status = RequestStatus.SUCCESS;

        geoLocation.data = action.payload.geoReport;
        geoLocation.meta.status = RequestStatus.SUCCESS;
      })

      .addCase(loadDashboardReportAsync.rejected, (state) => {
        const { stats, platformStats, geoLocation } = state;

        stats.meta.status = RequestStatus.ERROR;
        platformStats.meta.status = RequestStatus.ERROR;
        geoLocation.meta.status = RequestStatus.ERROR;
      });
  },
});

export const DashboardActions = dashboardSlice.actions;

export const dashboardReducer = dashboardSlice.reducer;
